/**
 * Bloqueo rápido.
 *
 * Aparece en Ajustes, dentro de la tarjeta de seguridad. Lo que se explica ahí es
 * exactamente lo que hace y lo que no: es una pantalla de bloqueo local, no una
 * credencial.
 *
 * Poner un candado y llamarlo «seguridad» sin más sería mentir, y esa mentira se paga
 * el día que alguien confía en ella de verdad.
 */

import { KeyRound, Lock, ShieldCheck } from 'lucide-react';
import { useState } from 'react';

import {
  PIN_MAX_LENGTH,
  PIN_MIN_LENGTH,
  clearPin,
  hasPin,
  pinCreatedAt,
  pinSupported,
  setPin,
} from '../lib/quicklock';
import { Field, Note } from './ui';

export function QuickLockSettings(): React.JSX.Element {
  const [enabled, setEnabled] = useState(() => hasPin());
  const [editing, setEditing] = useState(false);
  const [pin, setPinValue] = useState('');
  const [repeat, setRepeat] = useState('');
  const [message, setMessage] = useState<{ tone: 'ok' | 'bad'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const supported = pinSupported();

  /** Guarda el PIN nuevo. */
  const save = async (): Promise<void> => {
    setMessage(null);

    if (pin !== repeat) {
      setMessage({ tone: 'bad', text: 'Los dos PIN no son iguales.' });
      return;
    }

    setBusy(true);
    try {
      await setPin(pin);
      setEnabled(true);
      setEditing(false);
      setPinValue('');
      setRepeat('');
      setMessage({ tone: 'ok', text: 'Listo. Se pedirá al abrir la aplicación en este dispositivo.' });
    } catch (error) {
      setMessage({
        tone: 'bad',
        text: error instanceof Error ? error.message : 'No se pudo guardar el PIN.',
      });
    } finally {
      setBusy(false);
    }
  };

  if (!supported) {
    return (
      <p className="text-sm text-ed-text-muted">
        Este navegador no permite guardar un PIN de forma segura, así que la opción no
        está disponible aquí. Todo lo demás funciona igual.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {!editing && (
        <div className="flex items-start gap-3">
          <span
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
              enabled ? 'bg-ed-success/15 text-ed-success' : 'bg-ed-bg-elevated text-ed-text-muted'
            }`}
            aria-hidden="true"
          >
            {enabled ? <ShieldCheck size={20} /> : <Lock size={20} />}
          </span>

          <div className="min-w-0 flex-1">
            <p className="font-medium">
              {enabled ? 'Bloqueo rápido activado' : 'Bloqueo rápido desactivado'}
            </p>
            <p className="mt-0.5 text-xs text-ed-text-muted">
              {enabled
                ? 'Se pide el PIN al abrir la aplicación en este dispositivo.'
                : 'Al abrir la aplicación se ven tus cifras directamente.'}
            </p>
            {enabled && pinCreatedAt() && (
              <p className="mt-1 text-[11px] text-ed-text-muted">
                Configurado el {new Date(pinCreatedAt()!).toLocaleDateString('es-CO')}.
              </p>
            )}
          </div>

          <div className="flex shrink-0 flex-col gap-2">
            <button
              type="button"
              className="ed-btn-secondary !min-h-[36px] !w-auto !px-3 text-xs"
              onClick={() => {
                setEditing(true);
                setMessage(null);
              }}
            >
              <KeyRound size={14} aria-hidden="true" />
              {enabled ? 'Cambiar' : 'Activar'}
            </button>

            {enabled && (
              <button
                type="button"
                className="ed-btn-ghost !min-h-[36px] !w-auto !px-3 text-xs"
                onClick={() => {
                  clearPin();
                  setEnabled(false);
                  setMessage({ tone: 'ok', text: 'Se quitó el bloqueo rápido.' });
                }}
              >
                Quitar
              </button>
            )}
          </div>
        </div>
      )}

      {editing && (
        <div className="space-y-3 rounded-ed border border-ed-bg-border/40 p-3">
          <Field
            label={`PIN (${PIN_MIN_LENGTH} a ${PIN_MAX_LENGTH} dígitos)`}
            htmlFor="pin-nuevo"
            hint="Solo números. Elige algo que recuerdes: si lo olvidas, entrarás con tu contraseña."
          >
            <input
              id="pin-nuevo"
              type="password"
              inputMode="numeric"
              autoComplete="new-password"
              className="ed-input ed-money text-center text-lg tracking-[0.4em]"
              value={pin}
              maxLength={PIN_MAX_LENGTH}
              onChange={(e) => setPinValue(e.target.value.replace(/\D/g, ''))}
            />
          </Field>

          <Field label="Repítelo" htmlFor="pin-repetir">
            <input
              id="pin-repetir"
              type="password"
              inputMode="numeric"
              autoComplete="new-password"
              className="ed-input ed-money text-center text-lg tracking-[0.4em]"
              value={repeat}
              maxLength={PIN_MAX_LENGTH}
              onChange={(e) => setRepeat(e.target.value.replace(/\D/g, ''))}
            />
          </Field>

          <div className="flex gap-2">
            <button
              type="button"
              className="ed-btn-primary flex-1 !min-h-[40px] text-sm"
              disabled={busy || pin.length < PIN_MIN_LENGTH || pin !== repeat}
              onClick={() => void save()}
            >
              {busy ? 'Guardando…' : 'Guardar el PIN'}
            </button>
            <button
              type="button"
              className="ed-btn-ghost !min-h-[40px] !w-auto !px-4 text-sm"
              onClick={() => {
                setEditing(false);
                setPinValue('');
                setRepeat('');
                setMessage(null);
              }}
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {message && (
        <p
          role="status"
          className={`rounded-ed px-3 py-2 text-sm ${
            message.tone === 'ok'
              ? 'border border-ed-success/40 bg-ed-success/10 text-ed-success'
              : 'border border-ed-coral-deep/40 bg-ed-coral-deep/10 text-ed-coral-light'
          }`}
        >
          {message.text}
        </p>
      )}

      <Note>
        Esto <strong>no sustituye a tu contraseña</strong>. Es una pantalla de bloqueo
        local: evita que quien coja tu teléfono vea tus cifras de un vistazo. El PIN no
        viaja a ningún servidor y no sirve para entrar desde otro dispositivo. Tus datos
        siguen protegidos por tu contraseña.
      </Note>
    </div>
  );
}
